// components/NodeToolbar.tsx - Updated version
import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Popover,
  Grid,
  Button,
  Paper,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Palette as PaletteIcon,
  EmojiEmotions as EmojiIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon, // Add this import
} from "@mui/icons-material";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import CommentPopover from "./CommentPopover"; // Add this import
import { NoteNodeData, Comment } from "../types"; // Update import

const colors = [
  "#fff59d",
  "#ffcdd2",
  "#c8e6c9",
  "#bbdefb",
  "#e1bee7",
  "#ffccbc",
  "#f8bbd9",
  "#b2dfdb",
  "#dcedc8",
  "#ffe0b2",
];

interface NodeToolbarProps {
  node: any;
  onColorChange: (nodeId: string, color: string) => void;
  onEmojiAdd: (nodeId: string, emoji: string, userId: string) => void;
  onEmojiToggle: (nodeId: string, emoji: string, userId: string) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
  viewport: { x: number; y: number; zoom: number };
  currentUserId: string;
  // Add these new props for comments
  onAddComment: (nodeId: string, text: string) => void;
  onAddCommentReaction: (
    nodeId: string,
    commentId: string,
    emoji: string
  ) => void;
  onRemoveCommentReaction: (
    nodeId: string,
    commentId: string,
    emoji: string,
    userId: string
  ) => void;
  currentUserName: string;
  currentUserColor: string;
}

const NodeToolbar: React.FC<NodeToolbarProps> = ({
  node,
  onColorChange,
  onEmojiAdd,
  onEmojiToggle,
  onDelete,
  onClose,
  viewport,
  currentUserId,
  onAddComment,
  onAddCommentReaction,
  onRemoveCommentReaction,
  currentUserName,
  currentUserColor,
}) => {
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);
  const [commentAnchor, setCommentAnchor] = useState<HTMLElement | null>(null); // Add this
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Calculate toolbar position below the node
  const getToolbarPosition = () => {
    if (!node) return { left: 0, top: 0 };

    const nodeWidth = node.style?.width || node.data?.width || 120;
    const nodeHeight = node.style?.height || node.data?.height || 80;

    const screenX = node.position?.x * viewport?.zoom + viewport?.x;
    const screenY = node.position?.y * viewport?.zoom + viewport?.y;

    return {
      left: screenX + (nodeWidth * viewport?.zoom) / 2 - 120, // Adjusted for more buttons
      top: screenY + nodeHeight * viewport?.zoom + 10,
    };
  };

  // Handle outside clicks to close toolbar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toolbarRef.current &&
        !toolbarRef.current.contains(event.target as Node) &&
        !colorAnchor &&
        !emojiAnchor &&
        !commentAnchor // Add this
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, colorAnchor, emojiAnchor, commentAnchor]); // Add commentAnchor

  const handleColorClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setColorAnchor(e.currentTarget);
  }, []);

  const handleColorClose = useCallback(() => {
    setColorAnchor(null);
  }, []);

  const handleColorSelect = useCallback(
    (color: string) => {
      setColorAnchor(null);
      onColorChange(node?.id, color);
    },
    [node?.id, onColorChange]
  );

  const handleEmojiClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setEmojiAnchor(e.currentTarget);
  }, []);

  const handleEmojiClose = useCallback(() => {
    setEmojiAnchor(null);
  }, []);

  const handleEmojiSelect = useCallback(
    (emojiData: EmojiClickData) => {
      setEmojiAnchor(null);

      const reactions = node.data?.reactions || [];
      const userHasReacted = reactions.some(
        (reaction: any) =>
          reaction.emoji === emojiData.emoji &&
          reaction.userId === currentUserId
      );

      if (userHasReacted) {
        onEmojiToggle(node.id, emojiData.emoji, currentUserId);
      } else {
        onEmojiAdd(node.id, emojiData.emoji, currentUserId);
      }
    },
    [node.id, node.data?.reactions, onEmojiAdd, onEmojiToggle, currentUserId]
  );

  // Add comment handlers
  const handleCommentClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setCommentAnchor(e.currentTarget);
  }, []);

  const handleCommentClose = useCallback(() => {
    setCommentAnchor(null);
  }, []);

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onDelete(node.id);
    },
    [node.id, onDelete]
  );

  const toolbarPosition = getToolbarPosition();
  const commentsCount = node.data?.comments?.length || 0;

  return (
    <>
      <Paper
        ref={toolbarRef}
        elevation={4}
        sx={{
          position: "absolute",
          left: toolbarPosition.left,
          top: toolbarPosition.top,
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          padding: 0.5,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(4px)",
          borderRadius: 2,
        }}
      >
        <Tooltip title="Add Comment">
          <IconButton
            size="small"
            onClick={handleCommentClick}
            sx={{
              width: 32,
              height: 32,
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.1)",
              },
            }}
          >
            <Badge badgeContent={commentsCount} color="primary" max={99}>
              <CommentIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Tooltip title="Add Emoji Reaction">
          <IconButton
            size="small"
            onClick={handleEmojiClick}
            sx={{
              width: 32,
              height: 32,
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.1)",
              },
            }}
          >
            <EmojiIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Change Color">
          <IconButton
            size="small"
            onClick={handleColorClick}
            sx={{
              width: 32,
              height: 32,
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.1)",
              },
            }}
          >
            <PaletteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete Node">
          <IconButton
            size="small"
            color="error"
            onClick={handleDelete}
            sx={{
              width: 32,
              height: 32,
              "&:hover": {
                backgroundColor: "rgba(244, 67, 54, 0.1)",
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Close">
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 32,
              height: 32,
              "&:hover": {
                backgroundColor: "rgba(244, 67, 54, 0.1)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Comment Popover */}
      <CommentPopover
        anchorEl={commentAnchor}
        open={Boolean(commentAnchor)}
        onClose={handleCommentClose}
        nodeId={node.id}
        comments={node.data?.comments || []}
        onAddComment={onAddComment}
        onAddCommentReaction={onAddCommentReaction}
        onRemoveCommentReaction={onRemoveCommentReaction}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
        currentUserColor={currentUserColor}
      />

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={handleColorClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box p={1.5} maxWidth={180}>
          <Grid container spacing={0.5}>
            {colors.map((color) => (
              <Grid item xs={2.4} key={color}>
                <Button
                  variant="outlined"
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    minWidth: 0,
                    width: "100%",
                    height: 24,
                    backgroundColor: color,
                    border: `2px solid ${
                      node.data?.color === color ? "#1976d2" : "#ccc"
                    }`,
                    "&:hover": {
                      backgroundColor: color,
                      borderColor: "#1976d2",
                    },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={handleEmojiClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box>
          <EmojiPicker
            onEmojiClick={handleEmojiSelect}
            width={300}
            height={400}
            searchDisabled={false}
            skinTonesDisabled={true}
            previewConfig={{
              showPreview: false,
            }}
          />
        </Box>
      </Popover>
    </>
  );
};

export default NodeToolbar;
